"use client";

import Link from "next/link";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { Logo } from "@/components/ui/logo";
import { MobileNav } from "@/components/mobile-nav";
import { Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import { siteConfig } from "@/config/site";

const services = [
  {
    title: "Group Health Insurance",
    href: "#services",
    description:
      "Custom plans from Blue Cross Blue Shield, United Healthcare, Iron Health, CEHAS Administrators",
  },
  {
    title: "Retirement Planning",
    href: "#services",
    description:
      "401k, 403b/457b, SEP, SIMPLE IRA with tax credits for new plans",
  },
  {
    title: "Life Insurance",
    href: "#services",
    description:
      "Group and individual policies from National Life Group and Mutual of Omaha",
  },
  {
    title: "Supplemental Coverage",
    href: "#services",
    description:
      "Accidental, Dental, Short and Long-term Disability, Critical Illness",
  },
];

export function Header() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="max-w-7xl mx-auto flex h-16 items-center px-4 md:px-6">
        <Link href="/" className="flex items-center space-x-2 mr-6">
          <Logo size="md" className="text-primary" />
          <span className="hidden lg:inline font-bold text-lg xl:text-xl font-montserrat">
            Leija Insurance Group
          </span>
        </Link>

        <NavigationMenu className="hidden lg:flex mx-auto">
          <NavigationMenuList>
            <NavigationMenuItem>
              <NavigationMenuLink
                asChild
                className={navigationMenuTriggerStyle()}
              >
                <Link href="/">Home</Link>
              </NavigationMenuLink>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuLink
                asChild
                className={navigationMenuTriggerStyle()}
              >
                <Link href="#about">About Nicholas</Link>
              </NavigationMenuLink>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuTrigger>Services</NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                  {services.map((service) => (
                    <ListItem
                      key={service.title}
                      title={service.title}
                      href={service.href}
                    >
                      {service.description}
                    </ListItem>
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuLink
                asChild
                className={navigationMenuTriggerStyle()}
              >
                <Link href="#process">Process</Link>
              </NavigationMenuLink>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuLink
                asChild
                className={navigationMenuTriggerStyle()}
              >
                <Link href="#contact">Contact</Link>
              </NavigationMenuLink>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>

        <div className="ml-auto flex items-center space-x-4">
          <Button variant="ghost" size="sm" className="hidden sm:flex" asChild>
            <Link href={siteConfig.contact.phoneHref}>
              <Phone className="mr-2 h-4 w-4" />
              {siteConfig.contact.phone}
            </Link>
          </Button>

          <Button className="hidden sm:inline-flex" asChild>
            <Link href="#contact">Get Quote</Link>
          </Button>

          <MobileNav isOpen={isOpen} onOpenChange={setIsOpen} />
        </div>
      </div>
    </header>
  );
}

const ListItem = ({
  className,
  title,
  children,
  href,
  ...props
}: React.ComponentPropsWithoutRef<"a"> & { title: string; href: string }) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          href={href}
          className={cn(
            "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground group",
            className,
          )}
          {...props}
        >
          <div className="text-sm font-medium leading-none">{title}</div>
          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground group-hover:text-accent-foreground">
            {children}
          </p>
        </Link>
      </NavigationMenuLink>
    </li>
  );
};
